import { Link, useNavigate, useParams } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAdminCourseEnrollments } from '@/hooks/use-admin'
import { useAuth } from '@/lib/auth'

export default function AdminCourseEnrollmentsPage() {
  const { courseId } = useParams<{ courseId: string }>()
  const { user: authUser } = useAuth()
  const navigate = useNavigate()
  const tenantSlug = authUser?.tenantSlug ?? ''

  const { data, isLoading, error } = useAdminCourseEnrollments(courseId)

  const result = data as any

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate(`/${tenantSlug}/admin/enrollments`)}>
          受講状況管理に戻る
        </Button>
        <p className="text-destructive">エラーが発生しました: {(error as Error).message}</p>
      </div>
    )
  }

  const courseInfo = result?.course
  const enrollments = result?.enrollments ?? []
  const totalEnrolled = result?.total ?? 0

  return (
    <div className="space-y-6">
      <Button variant="outline" onClick={() => navigate(`/${tenantSlug}/admin/enrollments`)}>
        受講状況管理に戻る
      </Button>

      {/* Course info card */}
      <Card>
        <CardHeader>
          <CardTitle>講座情報: {courseInfo?.title || courseId}</CardTitle>
          <CardDescription>
            ID: {courseInfo?.id}
            {courseInfo?.category && (
              <>
                <br />
                カテゴリ: {courseInfo.category}
              </>
            )}
            {courseInfo?.description && (
              <>
                <br />
                説明: {courseInfo.description}
              </>
            )}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>登録者数</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{totalEnrolled}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>完了者数</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">
              {enrollments.filter((e: any) => e.status === 'completed').length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>受講中</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">
              {enrollments.filter((e: any) => e.status !== 'completed').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Enrolled users table */}
      <Card>
        <CardHeader>
          <CardTitle>受講者一覧</CardTitle>
          <CardDescription>{totalEnrolled}人の受講者が登録されています。</CardDescription>
        </CardHeader>
        <CardContent>
          {enrollments.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ユーザーID</TableHead>
                  <TableHead>ステータス</TableHead>
                  <TableHead>登録日</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrollments.map((enrollment: any) => (
                  <TableRow key={enrollment.id}>
                    <TableCell className="font-mono text-sm">
                      <Link
                        to={`/${tenantSlug}/admin/enrollments/user/${enrollment.userId}`}
                        className="hover:underline text-primary"
                      >
                        {enrollment.userId}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant={enrollment.status === 'completed' ? 'default' : 'secondary'}>
                        {enrollment.status === 'completed' ? '完了' : '受講中'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {enrollment.enrolledAt
                        ? new Date(enrollment.enrolledAt).toLocaleDateString('ja-JP')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(`/${tenantSlug}/admin/enrollments/user/${enrollment.userId}`)
                        }
                      >
                        ユーザー詳細
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground">このコースにはまだ受講者が登録されていません。</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
